import React from 'react';
import { ClientField, ClientFieldImageChoice, ClientFieldVideo, ClientFieldAppleMusicPlaylist, ClientFieldMultipleChoice } from 'shared/types/appTypes';
import ShortTextField from './ShortTextField';
import LongTextField from './LongTextField';
import NumberField from './NumberField';
import BooleanField from './BooleanField';
import AddressField from './AddressField';
import DateField from './DateField';
import DateRangeField from './DateRangeField';
import TimeField from './TimeField';
import TimeRangeField from './TimeRangeField';
import MultipleChoiceField from './MultipleChoiceField';
import ImageChoiceField from './ImageChoiceField';
import VideoField from './VideoField';
import AppleMusicPlaylistField from './AppleMusicPlaylistField';

interface FormFieldRendererProps {
  field: ClientField;
  value: any;
  onChange: (value: any) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({ field, value, onChange, autoFocus, disabled }) => {
  switch (field.type) {
    case 'shortText':
      return <ShortTextField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'longText':
      return <LongTextField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'number':
      return <NumberField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'boolean':
      return <BooleanField value={value} onChange={onChange} disabled={disabled} />;

    case 'address':
      return <AddressField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'date':
      return <DateField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'dateRange':
      return <DateRangeField value={value || { start: null, end: null }} onChange={onChange} disabled={disabled} />;

    case 'time':
      return <TimeField value={value} onChange={onChange} autoFocus={autoFocus} disabled={disabled} />;

    case 'timeRange':
      return <TimeRangeField value={value || { start: null, end: null }} onChange={onChange} disabled={disabled} />;

    case 'multipleChoice':
      return (
        <MultipleChoiceField
          value={value}
          onChange={onChange}
          choices={(field as ClientFieldMultipleChoice).choices || []}
          allowOtherChoice={(field as ClientFieldMultipleChoice).allowOtherChoice}
          checkAllThatApply={(field as ClientFieldMultipleChoice).checkAllThatApply}
          disabled={disabled}
        />
      );

    case 'imageChoice':
      return (
        <ImageChoiceField
          value={value}
          onChange={onChange}
          options={(field as ClientFieldImageChoice).options || []}
          disabled={disabled}
        />
      );

    case 'video':
      return <VideoField playbackUrl={(field as ClientFieldVideo).playbackUrl} />;

    case 'appleMusicPlaylist': {
      const amField = field as ClientFieldAppleMusicPlaylist;
      return (
        <AppleMusicPlaylistField
          playlistUrl={amField.playlistUrl}
          minChoices={amField.minChoices}
          maxChoices={amField.maxChoices}
          value={value || []}
          onChange={onChange}
          disabled={disabled}
        />
      );
    }

    default:
      return null;
  }
};

export default FormFieldRenderer;
